import { useMemo, useState } from "react";
import { useVariant } from "@unleash/proxy-client-react";
import { Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";

import { ICard } from "../../../../api/cards/types";
import { UnleashFlags } from "../../../../constants";
import { RadioCheckedIcon, RadioUncheckedIcon } from "../../../icons";
import { getPaymentMethodName, isStringWithNumber } from "../../../../utils";

interface Props {
  card: ICard;
  setMainProduct: (cardId: string) => Promise<void>;
  isProductStatusChanging: boolean;
}

export function CardItemMain({ card, setMainProduct, isProductStatusChanging }: Props) {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const commissionVariant = useVariant(UnleashFlags.dfPaymentsCommission);
  const commissionRawValue = commissionVariant.payload?.value;
  const commissionValue = isStringWithNumber(commissionRawValue) ? `${commissionRawValue}%` : "";
  const commissionText = card.terminalType === "PAID" ? `Комиссия ${commissionValue}` : "";

  const productName = getPaymentMethodName(card);
  const productNameVariant = card.statusCode === "LINKED" ? "brand" : "gray";
  const showSpinner = isUpdating || card.statusCode === "NOT_LINKED";
  const spinnerVariant = isUpdating ? "purple" : "gray";
  const isDisabled = isProductStatusChanging || card.isMainProduct || card.statusCode !== "LINKED";

  const { borderColor, RadioIcon, statusVariant } = useMemo(() => {
    let borderColor = "white";
    let Icon = RadioUncheckedIcon;
    let statusVariant: "gray" | "purple" = "gray";

    if (card.isMainProduct && card.statusCode === "LINKED") {
      borderColor = "purple.500";
      Icon = RadioCheckedIcon;
      statusVariant = "purple";
    }

    return { borderColor, RadioIcon: Icon, statusVariant } as const;
  }, [card.isMainProduct]);

  const statusText = useMemo(() => {
    if (isUpdating) {
      return "Меняем основной способ оплаты...";
    }

    if (card.statusCode === "LINKED") {
      return card.isMainProduct ? "Основной способ оплаты" : "Выбрать основным";
    }

    if (card.statusCode === "NOT_LINKED") {
      return "Ожидает проверки";
    }
  }, [card.isMainProduct, card.statusCode, isUpdating]);

  const onSetMainProduct = async () => {
    if (isDisabled) {
      return;
    }

    setIsUpdating(true);

    try {
      await setMainProduct(card.productId ?? "");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Button
      h="100%"
      w="100%"
      pl="19px"
      bg="white"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="12px"
      display="flex"
      justifyContent="flex-start"
      pointerEvents={isDisabled ? "none" : "auto"}
      cursor={isDisabled ? "default" : "pointer"}
      onClick={onSetMainProduct}
      isDisabled={isDisabled}
      _disabled={{ bg: "white", color: "inherit", opacity: 1, cursor: "default" }}
      _hover={{ bg: "white" }}
    >
      {showSpinner
        ? <Spinner variant={spinnerVariant} />
        : <RadioIcon />}

      <VStack spacing={0} alignItems="flex-start" ml="8px">
        <HStack spacing="7px">
          <Text variant={productNameVariant} lineHeight="22px">{productName}</Text>
          <Text fontSize="12px" lineHeight="22px">{commissionText}</Text>
        </HStack>

        <Text variant={statusVariant} fontSize="xs" lineHeight="16px">{statusText}</Text>
      </VStack>
    </Button>
  );
}
